import { ChevronRight } from 'lucide-react'
import type React from 'react'
import { Portal } from '@/components/ui/portal'
import { Toolbar, ToolbarButton } from '@/components/ui/toolbar'
import { CollapsedButton } from './CollapsedButton'
import { TIMING } from './constants'
import { ToolbarProvider, useToolbarContextValue } from './ToolbarContext'
import { ToolbarItem } from './ToolbarItem'
import { useToolbarCollapse } from './useToolbarCollapse'
import './CollapsibleToolbar.css'

export interface CollapsibleToolbarProps {
	children: React.ReactNode
	position?: 'bottom-right' | 'bottom-left' | 'bottom-center'
	collapsedIcon?: React.ReactNode
	collapseTooltip?: string
	expandLabel?: string
	collapseAriaLabel?: string
	canCollapse?: boolean
}

/**
 * Generic collapsible toolbar component
 * Handles collapse/expand animations and state management
 * Accepts any children via composition - wrap items in ToolbarItem for auto-animation
 */
export const CollapsibleToolbar: React.FC<CollapsibleToolbarProps> = ({
	children,
	position = 'bottom-right',
	collapsedIcon,
	collapseTooltip,
	expandLabel,
	collapseAriaLabel,
	canCollapse = true,
}) => {
	const { isCollapsed, isAnimating, showContent, collapse, expand } = useToolbarCollapse({
		canCollapse,
	})
	const contextValue = useToolbarContextValue(isAnimating, showContent)

	if (canCollapse && isCollapsed) {
		return (
			<CollapsedButton
				onExpand={expand}
				position={position}
				icon={collapsedIcon}
				label={expandLabel}
			/>
		)
	}

	return (
		<Portal>
			<div className={`collapsible-toolbar collapsible-toolbar--${position}`}>
				<ToolbarProvider value={contextValue}>
					<Toolbar
						id="collapsible-toolbar"
						className={`collapsible-toolbar__bar ${isAnimating ? 'pointer-events-none' : ''}`}
						data-animating={isAnimating}
						data-expanded={showContent}
						style={{
							animation: isAnimating
								? showContent
									? `barExpand ${TIMING.BAR_EXPAND_MS}ms cubic-bezier(0.4, 0, 0.2, 1) forwards`
									: `barCollapse ${TIMING.BAR_EXPAND_MS}ms cubic-bezier(0.4, 0, 0.2, 1) forwards`
								: 'none',
							width: showContent && !isAnimating ? 'fit-content' : undefined,
							maxWidth: showContent ? '480px' : '40px',
						}}
					>
						{canCollapse && (
							<ToolbarItem>
								<ToolbarButton
									onClick={collapse}
									aria-expanded={true}
									aria-controls="collapsible-toolbar"
									aria-label={collapseAriaLabel || 'Collapse toolbar'}
									tooltip={collapseTooltip}
									variant="ghost"
									className="collapsible-toolbar__collapse-btn"
								>
									<ChevronRight className="w-4 h-4" />
								</ToolbarButton>
							</ToolbarItem>
						)}
						{children}
					</Toolbar>
				</ToolbarProvider>
			</div>
		</Portal>
	)
}
